/**
 * getHistorySourcePrefillInputs.js
 *
 * Extracts the underlying form question(s), selected answers, and available
 * options for a History line item — used by the magnifying-glass source
 * dialog (mirrors CIQuestionPanel.getRawPrefillInputs for modular form instances).
 */
import {
  isModularQuestionSkipped,
  normalizeAnswersToNested,
  normalizeModularAnswerValue,
} from '../../5_patientOverview/utils/buildModularPatientOverviewModel';
import { normalizeRawAnswer, buildDisplayAnswer, coerceDisplayText } from '../../../../utils/formDisplayUtils';
import { resolveFormInstanceForDisplay } from '../../../../utils/resolveFormTemplates';
import { findSourceFormQuestionForProviderVar } from '../../../serviceUsers/3_forms/functions/AutismPrefillFunctions';

/**
 * Check whether a var name exists as a real form subquestion in the supplied instances.
 */
function varNameExistsInFormInstances(varName, formInstances, patientFirstName = '') {
  for (const formInstance of Object.values(formInstances || {})) {
    const displayInstance = patientFirstName
      ? resolveFormInstanceForDisplay(formInstance, patientFirstName)
      : formInstance;

    const formDef = displayInstance?.formDefinition;
    if (!formDef?.formSubsections) continue;

    for (const subsection of Object.values(formDef.formSubsections)) {
      for (const question of Object.values(subsection.questions || {})) {
        for (const subquestion of Object.values(question.subquestions || {})) {
          if (subquestion.serviceUserVarName === varName) return true;
        }
      }
    }
  }

  return false;
}

/**
 * Some history line items use composite prefill keys (e.g. informant_sleep,
 * informant_selfCare, selfCare) that do not exist as literal form fields.
 * Expand those to the underlying subquestion var names so the source dialog
 * can show the original questionnaire answers.
 */
function expandCompositeVarNames(varNames, formInstances, patientFirstName = '') {
  const expanded = [];
  const seen = new Set();

  const addVarName = (name) => {
    if (!name || seen.has(name)) return;
    seen.add(name);
    expanded.push(name);
  };

  for (const varName of varNames || []) {
    if (varNameExistsInFormInstances(varName, formInstances, patientFirstName)) {
      addVarName(varName);
      continue;
    }

    const providerVarName = varName.startsWith('informant_')
      ? varName.slice('informant_'.length)
      : varName;

    const source = findSourceFormQuestionForProviderVar(
      formInstances,
      providerVarName,
      patientFirstName,
    );

    if (source) {
      Object.entries(source.question.subquestions || {}).forEach(([sqKey, sq]) => {
        if (Number.isNaN(parseInt(sqKey, 10))) return;
        addVarName(sq?.serviceUserVarName);
      });
      continue;
    }

    addVarName(varName);
  }

  return expanded;
}

/**
 * When form instances are missing or stale but the history stage already has a
 * formatted prefill string, build a single dialog row from that stored text.
 * Mirrors CIQuestionPanel's dialogPrefillText fallback for demo/legacy patients.
 *
 * @param {string|null} displayPrefillText - Formatted text shown in the column
 * @param {string} [lineItemTitle] - History question title for context
 * @returns {object|null} Synthetic source row, or null when no stored text exists
 */
export function buildStoredPrefillFallbackRow(displayPrefillText, lineItemTitle = '') {
  const text = (coerceDisplayText(displayPrefillText) || '').trim();
  if (!text) return null;

  return {
    formName: 'Assessment history',
    questionTitle: null,
    question: lineItemTitle || 'History line item',
    answer: text,
    hasAnswer: true,
    inputType: '',
    options: [],
    rawAnswer: null,
    wasSkipped: false,
    skipButtonText: null,
    isStoredPrefillFallback: true,
  };
}

/**
 * Resolve dialog rows: prefer live form-instance data; fall back to stored prefill.
 */
export function resolveHistorySourceDialogRows(
  varNames,
  formInstances,
  displayPrefillText,
  patientFirstName = '',
  lineItemTitle = '',
) {
  const fromForms = getHistorySourcePrefillInputs(varNames, formInstances, patientFirstName);
  if (fromForms.length > 0) return fromForms;

  const fallback = buildStoredPrefillFallbackRow(displayPrefillText, lineItemTitle);
  return fallback ? [fallback] : [];
}

/**
 * @param {string[]} varNames - serviceUserVarNames that feed this history question
 * @param {object} formInstances - formKey -> formInstance map
 * @param {string} [patientFirstName] - When set, resolves {{patientFirstName}} tokens
 *   in informant form display text (question wording and options).
 * @returns {Array<object>} Source rows for the source-data dialog
 */
export function getHistorySourcePrefillInputs(varNames, formInstances, patientFirstName = '') {
  if (!varNames?.length || !formInstances) return [];

  const resolvedVarNames = expandCompositeVarNames(varNames, formInstances, patientFirstName);
  const results = [];

  Object.entries(formInstances).forEach(([formKey, formInstance]) => {
    const displayInstance = patientFirstName
      ? resolveFormInstanceForDisplay(formInstance, patientFirstName)
      : formInstance;

    const formDef = displayInstance?.formDefinition;
    if (!formDef?.formSubsections) return;

    const rawAnswers = formInstance.answers || {};
    const nestedAnswers = normalizeAnswersToNested(formDef.formSubsections, rawAnswers);

    Object.values(formDef.formSubsections).forEach((subsection) => {
      Object.entries(subsection.questions || {}).forEach(([qKey, question]) => {
        const questionAnswers = nestedAnswers[qKey];
        // Read from the raw map: the flat `_skip_<questionVarName>` marker
        // GenericFormPage writes is keyed by the question's var name, which
        // the nested view cannot carry (see isModularQuestionSkipped).
        const wasSkipped = isModularQuestionSkipped(rawAnswers, qKey, question);

        Object.entries(question.subquestions || {}).forEach(([sqKey, subquestion]) => {
          if (Number.isNaN(parseInt(sqKey, 10))) return;

          const varName = subquestion.serviceUserVarName;
          if (!resolvedVarNames.includes(varName)) return;

          const raw = questionAnswers?.[varName];
          const normalized = normalizeModularAnswerValue(raw, subquestion.inputType);
          const displayNormalized = normalizeRawAnswer(normalized, subquestion.otherAnswer);
          const allOptions = [
            ...(subquestion.options || []),
            ...(subquestion.noneOptions || []),
          ];

          results.push({
            formName: displayInstance.formTitle || formKey,
            questionTitle: question.questionTitle,
            question: subquestion.subquestionDisplayText || subquestion.subquestionText || varName,
            answer: buildDisplayAnswer(displayNormalized),
            hasAnswer: displayNormalized !== null && displayNormalized !== undefined && displayNormalized !== '',
            inputType: subquestion.inputType || '',
            options: allOptions,
            rawAnswer: displayNormalized,
            wasSkipped,
            skipButtonText: question.skipButtonText,
          });
        });
      });
    });
  });

  return results;
}
